import { UseQueryResult } from "@tanstack/react-query";
import {
  resetOrderIfLimited,
  resetOrderToMostRelevantIfApplicable
} from "PFApp/booking/parts/overview/use_tab_data.utils";
import { useFiltersV2Select, useOnFiltersUpdate, useOnFilterUpdate } from "PFApp/components/filters_v2";
import { useGrowlStoreActions } from "PFApp/stores/growl_store";
import {
  useWorkforceEntries,
  WorkforceResponse
} from "PFCore/hooks/queries/bookings/workforce/use_workforce_entries";
import { OrderOption } from "PFTypes";
import { CalendarRange } from "PFTypes/calendar_range";
import { KnownFiltersV2 } from "PFTypes/filters_v2";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { useWorkforceDataFilters } from "./use_workforce_data_filters";

const PER_PAGE = 15;

type UseWorkforceData = {
  dateRange: CalendarRange;
  enabled: boolean;
  page: number;
  resetPagination: VoidFunction;
  selectedOrder: OrderOption | null;
  setSelectedOrder: (order: OrderOption | null) => void;
};

export const useWorkforceData = ({
  dateRange,
  enabled,
  page,
  resetPagination,
  selectedOrder,
  setSelectedOrder
}: UseWorkforceData): UseQueryResult<WorkforceResponse, { status: number }> => {
  const { searchId } = useFiltersV2Select("searchId");

  const { showGrowl } = useGrowlStoreActions();
  const { t } = useTranslation("bookingModule");

  const { apiFilters, areApiFiltersReady, syncFiltersMeta } = useWorkforceDataFilters({ enabled });

  useOnFilterUpdate(KnownFiltersV2.Term, (filter) => {
    if (!enabled || filter.isChildren) {
      return;
    }
    resetOrderToMostRelevantIfApplicable(filter, selectedOrder, setSelectedOrder);
  });

  useOnFiltersUpdate(() => {
    if (!enabled) {
      return;
    }
    resetPagination();
    resetOrderIfLimited(selectedOrder, () => {
      setSelectedOrder(null);
      showGrowl({
        message: t("sortingReset"),
        kind: "alert"
      });
    });
  });

  const query = useWorkforceEntries({
    page,
    perPage: PER_PAGE,
    order: selectedOrder,
    filters: apiFilters,
    dateRange,
    previousSearchId: searchId as number | undefined,
    options: {
      enabled: enabled && areApiFiltersReady
    }
  });
  const { data, error } = query;

  useEffect(() => {
    if (enabled) {
      syncFiltersMeta(data?.meta);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.meta?.filtersV2, data?.meta?.searchId, enabled, apiFilters]);

  useEffect(() => {
    if (!!error && !error.status) {
      showGrowl({
        message: t("workforce.fetchingError"),
        kind: "error"
      });
      setSelectedOrder(null);
    }
  }, [error]);

  return query;
};
